import { Location } from '@angular/common';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { sortCaseInsensitive } from '../../../server/services/utils.service';
import type { OrgTag, OrgUser } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import {
  FilterTaskTagDialogComponent,
  type FilterTaskTagDialogComponentData,
} from '../filter-task-tag-dialog/filter-task-tag-dialog.component';
import {
  FilterTaskUserDialogComponent,
  type FilterTaskUserDialogComponentData,
} from '../filter-task-user-dialog/filter-task-user-dialog.component';
import { TagService } from '../tag.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-filter-task-buttons',
  templateUrl: './filter-task-buttons.component.html',
  styleUrls: ['./filter-task-buttons.component.scss'],
  standalone: false,
})
export class FilterTaskButtonsComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private dialog = inject(MatDialog);
  private utilsService = inject(UtilsService);
  private route = inject(ActivatedRoute);
  private tagService = inject(TagService);
  private location = inject(Location);

  orgId: string;
  spaceId: string;
  taskId?: string;
  users: DeepReadonly<OrgUser>[] = [];
  userId: string | undefined;
  userSelected: DeepReadonly<OrgUser> | undefined;
  noUser = false;
  usersRemaining: DeepReadonly<OrgUser>[] = [];

  tags: OrgTag[] = [];
  noTags = false;
  tagIds: string[] = [];
  tagsSelected: OrgTag[] = [];
  tagsRemaining: OrgTag[] = [];

  preserveTaskIdPath: string;
  removeTaskIdPath: string;

  locationVoidFunction?: VoidFunction;

  subscriptions = new Subscription();

  ngOnInit(): void {
    const { orgId, spaceId, taskId } = this.route.snapshot.params;
    this.orgId = orgId;
    this.spaceId = spaceId;
    this.taskId = taskId;

    const { userId, tagIds } = this.utilsService.taskFilterQueryParams(
      this.route.snapshot.queryParams
    );

    if (userId === null) this.noUser = true;
    else this.userId = userId;

    // We use this method because clicking on the tasks (changing the url) does
    // not trigger a pop state and hence does not trigger other location and
    // router subscriptions.
    this.locationVoidFunction = this.location.onUrlChange((val) => {
      this.preserveTaskIdPath = val.split('?')[0];
      this.removeTaskIdPath = val.split('?')[0].split('/task/')[0];
    });
    this.preserveTaskIdPath = this.location.path().split('?')[0];
    this.removeTaskIdPath = this.location
      .path()
      .split('?')[0]
      .split('/task/')[0];

    this.users = this.authUserService
      .getSpaceUserData({ orgId, spaceId })
      .sort((a, b) => sortCaseInsensitive(a.name, b.name));
    this.users.forEach((user) => {
      if (user._id === this.userId) this.userSelected = user;
      else this.usersRemaining.push(user);
    });

    if (tagIds === null) {
      this.noTags = true;
      this.tagIds = [];
    } else {
      this.noTags = false;
      this.tagIds = tagIds;
    }

    const sub = this.tagService
      .allTags({ orgId, spaceId })
      .subscribe((tags) => {
        this.tagsSelected = [];
        this.tagsRemaining = [];
        tags.sort((a, b) => sortCaseInsensitive(a.name, b.name));
        tags.forEach((tag) => {
          if (this.tagIds.includes(tag._id)) this.tagsSelected.push(tag);
          else this.tagsRemaining.push(tag);
        });
        this.tags = tags;
      });
    this.subscriptions.add(sub);
  }

  deselectTagLink(tagId: string) {
    return this.tagIds.filter((t) => t !== tagId);
  }

  launchUserDialog() {
    const dialogConfig = new MatDialogConfig();
    const { orgId, spaceId, userId, noUser } = this;
    const data: FilterTaskUserDialogComponentData = {
      orgId,
      spaceId,
      userId: noUser ? null : userId,
    };
    dialogConfig.data = data;
    dialogConfig.position = { top: '75px' }; // #filterAutocompleteHeight
    const dialogRef = this.dialog.open(
      FilterTaskUserDialogComponent,
      dialogConfig
    );

    const sub = dialogRef.afterClosed().subscribe((result) => {
      //
    });
    this.subscriptions.add(sub);
  }

  launchTagDialog() {
    const dialogConfig = new MatDialogConfig();
    const { orgId, spaceId, tagIds, noTags } = this;
    const data: FilterTaskTagDialogComponentData = {
      orgId,
      spaceId,
      tagIds,
      noTags,
    };
    dialogConfig.data = data;
    dialogConfig.position = { top: '75px' }; // #filterAutocompleteHeight
    const dialogRef = this.dialog.open(
      FilterTaskTagDialogComponent,
      dialogConfig
    );

    const sub = dialogRef.afterClosed().subscribe((result) => {
      //
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    if (this.locationVoidFunction) this.locationVoidFunction();
    this.subscriptions.unsubscribe();
  }
}
